import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, CreditCard, Lightning, XCircle, CurrencyDollar, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const TABS = [
  { id: "trial_active", label: "Free Trials", icon: Lightning, color: "#22C55E" },
  { id: "paid_subscribers", label: "Paid", icon: CreditCard, color: "#007AFF" },
  { id: "new_signups", label: "New Signups (30d)", icon: Users, color: "#A855F7" },
  { id: "inactive", label: "Inactive / Canceled", icon: XCircle, color: "#FF3B30" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const daysRemaining = (iso) => {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const diff = (dt - new Date()) / (24 * 3600 * 1000);
    return Math.max(0, Math.ceil(diff));
  } catch { return null; }
};

function Kpi({ label, value, color = "#fff", testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ActionModal({ action, user, onClose, onDone }) {
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

  if (!action || !user) return null;

  const config = {
    extend: {
      title: "Extend Free Trial",
      copy: `Extend ${user.email}'s free trial. Their card will NOT be charged until the new end date. A confirmation email is sent to the subscriber.`,
      submitLabel: "Extend trial",
      submitColor: "#22C55E",
      needsDays: true,
      defaultDays: 7,
    },
    cancel: {
      title: "Cancel Subscription",
      copy: `Immediately cancel ${user.email}'s subscription. They keep access through today only, then lose pick access. Stripe sub is closed (no period-end grace). A confirmation email is sent.`,
      submitLabel: "Cancel subscription",
      submitColor: "#FF3B30",
      needsDays: false,
    },
    reinstate: {
      title: "Reinstate Account",
      copy: `Grant ${user.email} a fresh free-access window. Their picks history resumes from where it left off and a welcome-back email is sent.`,
      submitLabel: "Reinstate user",
      submitColor: "#007AFF",
      needsDays: true,
      defaultDays: 7,
    },
  }[action];

  const submit = async () => {
    setSubmitting(true);
    try {
      let res;
      if (action === "extend") {
        res = await api.post("/admin/billing/extend-trial", { email: user.email, days });
      } else if (action === "cancel") {
        res = await api.post("/admin/billing/cancel-subscription", { email: user.email });
      } else if (action === "reinstate") {
        res = await api.post("/admin/billing/reinstate", { email: user.email, grant_days: days });
      }
      const newEnd = res?.data?.trial_ends_at || res?.data?.trial_until;
      if (action === "extend") {
        toast.success(`Trial extended. New end date: ${fmt(newEnd)}`, { duration: 7000, description: "Confirmation email sent to subscriber." });
      } else if (action === "cancel") {
        toast.success("Subscription canceled.", { duration: 5000, description: "Confirmation email sent to subscriber." });
      } else if (action === "reinstate") {
        toast.success(`Reinstated. Free access until: ${fmt(newEnd)}`, { duration: 7000, description: "Welcome-back email sent to subscriber." });
      }
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="admin-action-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-md bg-[#121212] border border-white/15 rounded-sm p-6 shadow-2xl"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: config.submitColor }}>{action}</div>
        <h2 className="font-display text-3xl leading-tight mb-3">{config.title}</h2>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">{config.copy}</p>

        {config.needsDays && (
          <div className="mb-5">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">Days</label>
            <input
              type="number"
              min={1}
              max={60}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(60, parseInt(e.target.value || "0", 10) || 1)))}
              className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-base font-mono-d"
              data-testid="admin-action-days"
              autoFocus
            />
            <div className="text-[10px] text-zinc-500 mt-1">Max 60 days per action.</div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="admin-action-cancel"
            className="px-4 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 rounded-sm transition-colors disabled:opacity-40"
          >
            Never mind
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="admin-action-confirm"
            style={{ borderColor: config.submitColor, color: config.submitColor }}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold border-2 hover:bg-white/5 rounded-sm transition-colors disabled:opacity-40"
          >
            {submitting ? "Working…" : config.submitLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function UserActions({ user, onAction }) {
  return (
    <div className="flex gap-1.5">
      {user.subscription_status === "trial" && (
        <button onClick={() => onAction("extend", user)} data-testid={`act-extend-${user.email}`}
          className="text-[9px] uppercase tracking-widest text-[#22C55E] hover:text-white px-2 py-1 border border-[#22C55E]/30 hover:border-white/30 rounded-sm transition-colors">
          Extend
        </button>
      )}
      {(user.subscription_status === "active" || user.subscription_status === "trial") && (
        <button onClick={() => onAction("cancel", user)} data-testid={`act-cancel-${user.email}`}
          className="text-[9px] uppercase tracking-widest text-[#FF3B30] hover:text-white px-2 py-1 border border-[#FF3B30]/30 hover:border-white/30 rounded-sm transition-colors">
          Cancel
        </button>
      )}
      {(user.subscription_status === "canceled" || user.subscription_status === "inactive") && (
        <button onClick={() => onAction("reinstate", user)} data-testid={`act-reinstate-${user.email}`}
          className="text-[9px] uppercase tracking-widest text-[#007AFF] hover:text-white px-2 py-1 border border-[#007AFF]/30 hover:border-white/30 rounded-sm transition-colors">
          Reinstate
        </button>
      )}
    </div>
  );
}

function UserTable({ users, columns, onAction, emptyMessage }) {
  if (!users || users.length === 0) {
    return <div className="text-center py-12 text-zinc-500 font-mono-d text-sm" data-testid="users-empty">{emptyMessage}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left py-2 pr-3">{c.label}</th>
            ))}
            <th className="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.email} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
              {columns.map((c) => (
                <td key={c.key} className="py-2.5 pr-3 text-zinc-300 align-top">{c.render ? c.render(u) : (u[c.key] || "—")}</td>
              ))}
              <td className="py-2.5 text-right">
                <UserActions user={u} onAction={onAction} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("trial_active");
  const [error, setError] = useState(null);
  const [modalAction, setModalAction] = useState(null); // { action, user } | null

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/subscriptions/overview");
      setData(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = useCallback((action, user) => {
    setModalAction({ action, user });
  }, []);

  if (loading && !data) {
    return <div className="max-w-[1400px] mx-auto px-6 py-10 text-zinc-500 font-mono-d" data-testid="subs-loading">Loading subscriptions…</div>;
  }
  if (error) {
    return <div className="max-w-[1400px] mx-auto px-6 py-10 text-[#FF3B30] font-mono-d" data-testid="subs-error">{error}</div>;
  }

  const kpis = data?.kpis || {};
  const currentList = data?.[tab] || [];

  const columnDefs = {
    trial_active: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "trial_ends_at", label: "Trial ends", render: (u) => {
        const target = u.trial_ends_at || u.trial_until;
        const days = daysRemaining(target);
        return <span>{fmtDate(target)} <span className={`ml-1 text-[10px] uppercase tracking-widest ${days <= 2 ? "text-[#FF3B30]" : "text-zinc-500"}`}>{days != null ? `${days}d left` : ""}</span></span>;
      } },
    ],
    paid_subscribers: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "last_paid_at", label: "Last paid", render: (u) => fmtDate(u.last_paid_at) },
      { key: "next_billing_at", label: "Next charge", render: (u) => fmtDate(u.next_billing_at) },
    ],
    new_signups: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "created_at", label: "Signed up", render: (u) => fmtDateTime(u.created_at) },
      { key: "subscription_status", label: "Status", render: (u) => (
        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 rounded-sm">
          {u.subscription_status}
        </span>
      ) },
    ],
    inactive: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "canceled_at", label: "Canceled", render: (u) => fmtDateTime(u.canceled_at) },
      { key: "cancel_reason", label: "Reason" },
    ],
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="admin-subscriptions-page">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Admin · Billing</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">SUBSCRIPTIONS</h1>
        </div>
        <button onClick={load} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1.5" data-testid="subs-refresh">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <Kpi label="Total Users" value={kpis.total_users || 0} testid="kpi-total" />
        <Kpi label="Paid Subs" value={kpis.paid_subscribers || 0} color="#007AFF" testid="kpi-paid" />
        <Kpi label="Active Trials" value={kpis.trial_active || 0} color="#22C55E" testid="kpi-trial" />
        <Kpi label="Inactive" value={kpis.inactive || 0} color="#FF3B30" testid="kpi-inactive" />
        <Kpi label="New (30d)" value={kpis.new_signups_30d || 0} color="#A855F7" testid="kpi-new" />
        <Kpi label="Est. MRR" value={`$${(kpis.mrr_estimate || 0).toLocaleString()}`} color="#22C55E" testid="kpi-mrr" />
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          const count = (data?.[t.id] || []).length;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`subs-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors ${active ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
            >
              <Icon size={14} weight={active ? "fill" : "regular"} style={{ color: active ? t.color : undefined }} />
              {t.label}
              <span className="ml-1 text-[10px] text-zinc-600">({count})</span>
            </button>
          );
        })}
      </div>

      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} key={tab} className="bg-[#121212] border border-white/10 rounded-sm p-4 sm:p-6">
        <UserTable
          users={currentList}
          columns={columnDefs[tab]}
          onAction={handleAction}
          emptyMessage={`No ${TABS.find((t) => t.id === tab)?.label.toLowerCase()} right now.`}
        />
      </motion.div>

      <div className="mt-10 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        <CurrencyDollar size={11} className="inline mb-0.5" /> MRR estimate = paid subscribers × $15 × 4.33 weeks/month
      </div>

      <ActionModal
        action={modalAction?.action}
        user={modalAction?.user}
        onClose={() => setModalAction(null)}
        onDone={load}
      />
    </div>
  );
}
